import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ApplyArAdvance,
  ArAdvance,
  ArAdvanceStatus,
  ArAgingResponse,
  ArBillingNote,
  ArBillingNoteStatus,
  ArCreditNote,
  ArCreditNoteStatus,
  ArCreditNoteWithLines,
  ArInvoiceListRow,
  ArInvoiceStatus,
  ArInvoiceVoid,
  ArInvoiceVoidStatus,
  ArInvoiceWithLines,
  ArPdc,
  ArPdcStatus,
  ArReceiptWithAllocations,
  BounceArPdc,
  ClearArPdc,
  CreateArAdvanceInput,
  CreateArBillingNoteInput,
  CreateArCreditNote,
  CreateArInvoice,
  CreateArInvoiceVoidInput,
  CreateArPdcInput,
  CreateArReceipt,
  CreditStatus,
  CustomerStatement,
  DepositArPdc,
  DsoDashboard,
  RefundArAdvance,
  StatementMode,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (same pattern as branches/customers/journals).
const invoicesKey = (companyId: string | undefined) => ['ar-invoices', companyId] as const;
const creditStatusKey = (companyId: string | undefined, customerId: string | undefined) =>
  ['ar-credit-status', companyId, customerId] as const;
const receiptsKey = (companyId: string | undefined) => ['ar-receipts', companyId] as const;
const agingKey = (companyId: string | undefined) => ['ar-aging', companyId] as const;
const creditNotesKey = (companyId: string | undefined) => ['ar-credit-notes', companyId] as const;
const invoiceVoidsKey = (companyId: string | undefined) => ['ar-invoice-voids', companyId] as const;
const billingNotesKey = (companyId: string | undefined) => ['ar-billing-notes', companyId] as const;
const pdcsKey = (companyId: string | undefined) => ['ar-pdcs', companyId] as const;
const advancesKey = (companyId: string | undefined) => ['ar-advances', companyId] as const;

export interface ArInvoiceFilters {
  status?: ArInvoiceStatus;
  customerId?: string;
  branchId?: string;
  departmentId?: string;
  dateFrom?: string;
  dateTo?: string;
  overdue?: boolean;
}

export function useArInvoices(companyId: string | undefined, filters: ArInvoiceFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  if (filters.branchId) qs.set('branchId', filters.branchId);
  if (filters.departmentId) qs.set('departmentId', filters.departmentId);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  if (filters.overdue) qs.set('overdue', 'true');
  const search = qs.toString();
  return useQuery({
    queryKey: [...invoicesKey(companyId), filters],
    queryFn: () =>
      api<ArInvoiceListRow[]>(`/companies/${companyId}/ar/invoices${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useArInvoice(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ar-invoices', companyId, id],
    queryFn: () => api<ArInvoiceWithLines>(`/companies/${companyId}/ar/invoices/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Credit status for the selected customer — used/available exposure vs the
// customer's creditLimit (AR-08). Drives the create-modal credit bar + the
// over-limit hold. Disabled until both ids are present; keyed by company +
// customer so a customer switch refetches and a post can invalidate it.
export function useArCreditStatus(companyId: string | undefined, customerId: string | undefined) {
  return useQuery({
    queryKey: creditStatusKey(companyId, customerId),
    queryFn: () =>
      api<CreditStatus>(
        `/companies/${companyId}/ar/invoices/customers/${customerId}/credit-status`,
      ),
    enabled: !!companyId && !!customerId,
  });
}

export function useCreateArInvoice(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateArInvoice; idempotencyKey?: string }) =>
      api<ArInvoiceWithLines>(`/companies/${companyId}/ar/invoices`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
      void qc.invalidateQueries({ queryKey: agingKey(companyId) });
      // A new invoice moves the customer's outstanding exposure — drop every
      // cached credit-status for this company (prefix match) so the create
      // modal's bar refetches next time.
      void qc.invalidateQueries({ queryKey: ['ar-credit-status', companyId] });
      // Dashboard's arAging is sourced from the same service — invalidate it too.
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export interface ArReceiptFilters {
  customerId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export function useArReceipts(companyId: string | undefined, filters: ArReceiptFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.customerId) qs.set('customerId', filters.customerId);
  if (filters.dateFrom) qs.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) qs.set('dateTo', filters.dateTo);
  const search = qs.toString();
  return useQuery({
    queryKey: [...receiptsKey(companyId), filters],
    queryFn: () =>
      api<ArReceiptWithAllocations[]>(
        `/companies/${companyId}/ar/receipts${search ? `?${search}` : ''}`,
      ),
    enabled: !!companyId,
  });
}

export function useCreateArReceipt(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateArReceipt; idempotencyKey?: string }) =>
      api<ArReceiptWithAllocations>(`/companies/${companyId}/ar/receipts`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: receiptsKey(companyId) });
      void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
      void qc.invalidateQueries({ queryKey: agingKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

export function useArAging(companyId: string | undefined, asOfDate?: string) {
  const qs = new URLSearchParams();
  if (asOfDate) qs.set('asOfDate', asOfDate);
  const search = qs.toString();
  return useQuery({
    queryKey: [...agingKey(companyId), { asOfDate: asOfDate ?? null }],
    queryFn: () =>
      api<ArAgingResponse>(`/companies/${companyId}/ar/aging${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

// ============================================================
// Customer Statement of Account (ใบแจ้งยอดบัญชีลูกหนี้) — Phase 3b (AR-11)
//
// Read-only report over existing data. `mode` toggles activity (from/to running
// ledger) vs open-item (asOf-aged outstanding invoices). Disabled until both ids
// are present; keyed by company + customer + query so a mode/date change
// refetches independently per company switch (same pattern as aging/credit-status).
// ============================================================

export interface StatementQueryInput {
  mode: StatementMode;
  from?: string;
  to?: string;
  asOf?: string;
}

export function useCustomerStatement(
  companyId: string | undefined,
  customerId: string | undefined,
  query: StatementQueryInput,
) {
  const qs = new URLSearchParams();
  qs.set('mode', query.mode);
  if (query.from) qs.set('from', query.from);
  if (query.to) qs.set('to', query.to);
  if (query.asOf) qs.set('asOf', query.asOf);
  const search = qs.toString();
  return useQuery({
    queryKey: ['ar-statement', companyId, customerId, query],
    queryFn: () =>
      api<CustomerStatement>(
        `/companies/${companyId}/ar/customers/${customerId}/statement?${search}`,
      ),
    enabled: !!companyId && !!customerId,
  });
}

// ============================================================
// AR Credit Notes (ใบลดหนี้) — Phase 2b
//
// Lifecycle: create DRAFT (no JV) → submit into the central approval engine →
// approve/reject/return steps. Final-step approval auto-posts the reversing JV
// and drops the source invoice's outstanding. The engine hooks mirror the
// ap-bill engine hooks exactly (doc-type-agnostic { step, comment|reason }).
// ============================================================

export interface ArCreditNoteFilters {
  status?: ArCreditNoteStatus;
  invoiceId?: string;
  customerId?: string;
}

export function useArCreditNotes(companyId: string | undefined, filters: ArCreditNoteFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.invoiceId) qs.set('invoiceId', filters.invoiceId);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...creditNotesKey(companyId), filters],
    queryFn: () =>
      api<ArCreditNote[]>(`/companies/${companyId}/ar/credit-notes${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useArCreditNote(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ar-credit-notes', companyId, id],
    queryFn: () => api<ArCreditNoteWithLines>(`/companies/${companyId}/ar/credit-notes/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Create a DRAFT credit note — no JV (the reversing JV books on final approval).
export function useCreateArCreditNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ data, idempotencyKey }: { data: CreateArCreditNote; idempotencyKey?: string }) =>
      api<ArCreditNoteWithLines>(`/companies/${companyId}/ar/credit-notes`, {
        method: 'POST',
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: creditNotesKey(companyId) });
    },
  });
}

// Engine-flow invalidation: credit notes (status/approvalStatus) + invoices &
// aging (a POSTED credit note drops the source invoice's outstanding) + the
// journal-entries cache (final approval books the reversing JV) + dashboard.
function invalidateArCnEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: creditNotesKey(companyId) });
  void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

// Submit a DRAFT / RETURNED credit note into the engine chain.
export function useSubmitArCreditNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ArCreditNoteWithLines>(
        'POST',
        `/companies/${companyId}/ar/credit-notes/${id}/submit`,
      ),
    onSuccess: () => invalidateArCnEngine(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the JV).
export function useApproveArCreditNoteStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<
        { step: number; comment?: string; sodOverrideReason?: string },
        ArCreditNoteWithLines
      >(
        'POST',
        `/companies/${companyId}/ar/credit-notes/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateArCnEngine(qc, companyId),
  });
}

// Reject the approval at a step (terminal — accounting status → VOID).
export function useRejectArCreditNoteStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ArCreditNoteWithLines>(
        'POST',
        `/companies/${companyId}/ar/credit-notes/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateArCnEngine(qc, companyId),
  });
}

// Return a PENDING credit note for revision (RETURNED — resubmittable).
export function useReturnArCreditNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ArCreditNoteWithLines>(
        'POST',
        `/companies/${companyId}/ar/credit-notes/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateArCnEngine(qc, companyId),
  });
}

// ============================================================
// AR Invoice Voids (ยกเลิกใบแจ้งหนี้) — Phase 2c (AR-07)
//
// Cancels a posted, UNTOUCHED invoice (no payments, no credit notes) via the
// central approval engine (4-eyes). Lifecycle: request DRAFT (no JV) → submit
// into the engine → approve/reject/return steps. Final-step approval posts the
// reversing JV (the inverse of the invoice's original JV) and flips the invoice
// to status VOID, so it drops out of aging/outstanding. Mirrors the credit-note
// hooks exactly (doc-type-agnostic { step, comment|reason }).
// ============================================================

export interface ArInvoiceVoidFilters {
  status?: ArInvoiceVoidStatus;
  invoiceId?: string;
  customerId?: string;
}

export function useArInvoiceVoids(
  companyId: string | undefined,
  filters: ArInvoiceVoidFilters = {},
) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.invoiceId) qs.set('invoiceId', filters.invoiceId);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...invoiceVoidsKey(companyId), filters],
    queryFn: () =>
      api<ArInvoiceVoid[]>(`/companies/${companyId}/ar/invoice-voids${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useArInvoiceVoid(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ar-invoice-voids', companyId, id],
    queryFn: () => api<ArInvoiceVoid>(`/companies/${companyId}/ar/invoice-voids/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Request a DRAFT void — no JV (the reversing JV books on final approval).
export function useRequestArInvoiceVoid(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateArInvoiceVoidInput) =>
      api<ArInvoiceVoid>(`/companies/${companyId}/ar/invoice-voids`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceVoidsKey(companyId) });
    },
  });
}

// Engine-flow invalidation: invoice voids (status/approvalStatus) + invoices &
// aging (a POSTED void flips the invoice to VOID → drops it out of aging) + the
// journal-entries cache (final approval books the reversing JV) + dashboard.
function invalidateArVoidEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: invoiceVoidsKey(companyId) });
  void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

// Submit a DRAFT / RETURNED void into the engine chain.
export function useSubmitArInvoiceVoid(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ArInvoiceVoid>(
        'POST',
        `/companies/${companyId}/ar/invoice-voids/${id}/submit`,
      ),
    onSuccess: () => invalidateArVoidEngine(qc, companyId),
  });
}

// Approve the current engine step (final-step approval auto-posts the reversal JV).
export function useApproveArInvoiceVoidStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      comment,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      comment?: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; comment?: string; sodOverrideReason?: string }, ArInvoiceVoid>(
        'POST',
        `/companies/${companyId}/ar/invoice-voids/${id}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: () => invalidateArVoidEngine(qc, companyId),
  });
}

// Reject the approval at a step (terminal — request status → VOID; invoice stays POSTED).
export function useRejectArInvoiceVoidStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ArInvoiceVoid>(
        'POST',
        `/companies/${companyId}/ar/invoice-voids/${id}/reject-step`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateArVoidEngine(qc, companyId),
  });
}

// Return a PENDING void for revision (RETURNED — resubmittable).
export function useReturnArInvoiceVoid(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      step,
      reason,
      sodOverrideReason,
    }: {
      id: string;
      step: number;
      reason: string;
      sodOverrideReason?: string;
    }) =>
      apiJson<{ step: number; reason: string; sodOverrideReason?: string }, ArInvoiceVoid>(
        'POST',
        `/companies/${companyId}/ar/invoice-voids/${id}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: () => invalidateArVoidEngine(qc, companyId),
  });
}

// ============================================================
// AR Billing Notes (ใบวางบิล) — Phase 3c (AR-09)
//
// Operational collection cover-document that bundles a customer's unpaid
// invoices for a scheduled payment / cheque pickup. Posts NO journal entry
// (RBAC-only, no approval engine). Lifecycle: DRAFT → ISSUED → CANCELLED + a
// derived `collected` flag. Mutations invalidate billing-notes + invoices +
// aging (a note doesn't move GL, but linked invoices' live outstanding drives
// the derived `collected` badge, so we keep the invoice/aging caches fresh).
// ============================================================

export interface ArBillingNoteFilters {
  status?: ArBillingNoteStatus;
  customerId?: string;
}

export function useBillingNotes(companyId: string | undefined, filters: ArBillingNoteFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...billingNotesKey(companyId), filters],
    queryFn: () =>
      api<ArBillingNote[]>(`/companies/${companyId}/ar/billing-notes${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useBillingNote(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ar-billing-notes', companyId, id],
    queryFn: () => api<ArBillingNote>(`/companies/${companyId}/ar/billing-notes/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Shared invalidation for the billing-note lifecycle — the note list (status)
// plus invoices + aging (linked invoices' live outstanding drives the derived
// `collected` badge). No journal-entries cache: a billing note never posts a JV.
function invalidateBillingNotes(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: billingNotesKey(companyId) });
  void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
}

// Create a DRAFT billing note bundling the customer's chosen open invoices.
export function useCreateBillingNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateArBillingNoteInput) =>
      api<ArBillingNote>(`/companies/${companyId}/ar/billing-notes`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => invalidateBillingNotes(qc, companyId),
  });
}

// Issue a DRAFT billing note (DRAFT → ISSUED).
export function useIssueBillingNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, ArBillingNote>(
        'POST',
        `/companies/${companyId}/ar/billing-notes/${id}/issue`,
      ),
    onSuccess: () => invalidateBillingNotes(qc, companyId),
  });
}

// Cancel a DRAFT / ISSUED billing note (→ CANCELLED) with an audited reason.
export function useCancelBillingNote(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiJson<{ reason: string }, ArBillingNote>(
        'POST',
        `/companies/${companyId}/ar/billing-notes/${id}/cancel`,
        { reason },
      ),
    onSuccess: () => invalidateBillingNotes(qc, companyId),
  });
}

// ============================================================
// AR Post-Dated Cheques (เช็คลงวันที่ล่วงหน้า / PDC) — Phase 4a (AR-10)
//
// Register/memo of customer post-dated cheques through their lifecycle
// (RECEIVED → DEPOSITED → CLEARED | BOUNCED). A PDC posts NO journal entry of
// its own; the AR stays outstanding until the cheque CLEARS, at which point the
// clear endpoint triggers the existing receipt flow (Dr Cash / Cr AR). RBAC-only
// (no approval engine). create/deposit/bounce invalidate the pdc list (+ invoices
// for create — its allocation snapshot reads live outstanding); clear posts a
// receipt, so it ALSO invalidates invoices + receipts + aging + journal + dashboard.
// ============================================================

export interface ArPdcFilters {
  status?: ArPdcStatus;
  customerId?: string;
}

export function useArPdcs(companyId: string | undefined, filters: ArPdcFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...pdcsKey(companyId), filters],
    queryFn: () => api<ArPdc[]>(`/companies/${companyId}/ar/pdcs${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

export function useArPdc(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ar-pdcs', companyId, id],
    queryFn: () => api<ArPdc>(`/companies/${companyId}/ar/pdcs/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Register a RECEIVED post-dated cheque (+ optional invoice allocations). No JV.
// Also invalidates invoices: the create captures an allocation snapshot keyed off
// the invoices' live outstanding, so keep that cache fresh.
export function useCreateArPdc(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateArPdcInput) =>
      api<ArPdc>(`/companies/${companyId}/ar/pdcs`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pdcsKey(companyId) });
      void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
    },
  });
}

// Deposit a RECEIVED cheque (RECEIVED → DEPOSITED). No JV — status only.
export function useDepositArPdc(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: DepositArPdc }) =>
      apiJson<DepositArPdc, ArPdc>(
        'POST',
        `/companies/${companyId}/ar/pdcs/${id}/deposit`,
        data ?? {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pdcsKey(companyId) });
    },
  });
}

// Clear a cheque (RECEIVED|DEPOSITED → CLEARED). This is the ONLY GL touch: the
// endpoint creates a receipt (Dr Cash / Cr AR) over the PDC's allocations, so the
// invoices/receipts/aging/journal/dashboard caches all move.
export function useClearArPdc(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ClearArPdc }) =>
      apiJson<ClearArPdc, ArPdc>('POST', `/companies/${companyId}/ar/pdcs/${id}/clear`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pdcsKey(companyId) });
      void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
      void qc.invalidateQueries({ queryKey: receiptsKey(companyId) });
      void qc.invalidateQueries({ queryKey: agingKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
      void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
    },
  });
}

// Bounce a cheque (RECEIVED|DEPOSITED → BOUNCED). No JV — AR was never settled, so
// it stays outstanding; only the status flips.
export function useBounceArPdc(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BounceArPdc }) =>
      apiJson<BounceArPdc, ArPdc>('POST', `/companies/${companyId}/ar/pdcs/${id}/bounce`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: pdcsKey(companyId) });
    },
  });
}

// ============================================================
// AR Customer Advances (เงินรับล่วงหน้า / deposits, AR-12) — Phase 4b
//
// Cash received from a customer BEFORE an invoice exists, booked against a
// liability (CUSTOMER_ADVANCE 20200002), not revenue/AR. An advance carries a
// derived `balance` (amount − applied − refunded); OPEN while balance > 0,
// CLOSED at 0. RBAC-only — real JVs at every step, no approval engine.
//   create  → Dr cash / Cr CUSTOMER_ADVANCE (the receive JV)
//   apply   → Dr CUSTOMER_ADVANCE / Cr AR_TRADE + bumps invoice.paidAmount
//   refund  → Dr CUSTOMER_ADVANCE / Cr cash (the remaining balance), closes
// apply/refund move both the advance list AND the invoice/aging/journal/dashboard
// caches (apply settles AR; refund touches GL); create moves advances + journal.
// ============================================================

export interface ArAdvanceFilters {
  status?: ArAdvanceStatus;
  customerId?: string;
}

export function useArAdvances(companyId: string | undefined, filters: ArAdvanceFilters = {}) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.customerId) qs.set('customerId', filters.customerId);
  const search = qs.toString();
  return useQuery({
    queryKey: [...advancesKey(companyId), filters],
    queryFn: () =>
      api<ArAdvance[]>(`/companies/${companyId}/ar/advances${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}

// Enriched advance detail (applications + the LIVE balance) — drives the apply
// modal's cap. Disabled until both ids are present.
export function useArAdvance(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: ['ar-advances', companyId, id],
    queryFn: () => api<ArAdvance>(`/companies/${companyId}/ar/advances/${id}`),
    enabled: !!companyId && !!id,
  });
}

// Create an OPEN advance — posts the receive JV (Dr cash / Cr CUSTOMER_ADVANCE).
// Invalidates advances + journal-entries (the receive JV).
export function useCreateArAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateArAdvanceInput) =>
      api<ArAdvance>(`/companies/${companyId}/ar/advances`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
      void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
    },
  });
}

// Shared invalidation for apply/refund — both touch GL (apply settles AR, refund
// returns cash) AND the advance balance, so refresh advances + invoices + aging +
// journal-entries + dashboard.
function invalidateArAdvanceGl(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
) {
  void qc.invalidateQueries({ queryKey: advancesKey(companyId) });
  void qc.invalidateQueries({ queryKey: invoicesKey(companyId) });
  void qc.invalidateQueries({ queryKey: agingKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['journal-entries', companyId] });
  void qc.invalidateQueries({ queryKey: ['dashboard', companyId] });
}

// Apply part of an advance balance to one of the customer's open invoices
// (Dr CUSTOMER_ADVANCE / Cr AR_TRADE + bumps invoice.paidAmount).
export function useApplyArAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ApplyArAdvance }) =>
      apiJson<ApplyArAdvance, ArAdvance>(
        'POST',
        `/companies/${companyId}/ar/advances/${id}/apply`,
        data,
      ),
    onSuccess: () => invalidateArAdvanceGl(qc, companyId),
  });
}

// Refund the remaining (unapplied) balance back to a company cash/bank COA
// (Dr CUSTOMER_ADVANCE / Cr cash); the advance closes.
export function useRefundArAdvance(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RefundArAdvance }) =>
      apiJson<RefundArAdvance, ArAdvance>(
        'POST',
        `/companies/${companyId}/ar/advances/${id}/refund`,
        data,
      ),
    onSuccess: () => invalidateArAdvanceGl(qc, companyId),
  });
}

// ============================================================
// AR DSO KPI dashboard (Days Sales Outstanding, AR-13) — Phase 4c
//
// Read-only, 0-schema analytics over invoices/receipts/credit-notes/voids. The
// server computes the overall company DSO, a 12-month trend, DSO by customer
// (slowest payers) + supporting tiles (BPDSO / ADD / aging split). GROSS basis
// (AR + sales both VAT-inclusive). Reuses the `ar.aging.read` permission. Keyed
// by company + query so a period (`days`/`asOf`) change refetches independently
// per company switch (same pattern as aging/statement). No mutations — the
// dashboard is derived, never written.
// ============================================================

export interface DsoQueryInput {
  asOf?: string;
  days?: number;
}

export function useDsoDashboard(companyId: string | undefined, query: DsoQueryInput = {}) {
  const qs = new URLSearchParams();
  if (query.asOf) qs.set('asOf', query.asOf);
  if (query.days !== undefined) qs.set('days', String(query.days));
  const search = qs.toString();
  return useQuery({
    queryKey: ['ar-dso', companyId, query],
    queryFn: () => api<DsoDashboard>(`/companies/${companyId}/ar/dso${search ? `?${search}` : ''}`),
    enabled: !!companyId,
  });
}
